import { classInstance } from '@/shared/helpers/helpers';
import { ModalGalleryMain } from './modal-gallery-main/modal-gallery-main';
import { ModalGalleryThumbs } from './modal-gallery-thumbs/modal-gallery-thumbs';

export class ModalGalleryMedia {
	private readonly element: HTMLElement;
	private readonly modalSelector = '.modal-gallery';
	private readonly mainSelector = '.modal-gallery-main';
	private readonly thumbsSelector = '.modal-gallery-thumbs';
	private readonly thumbsPrevButtonSelector = '.modal-gallery-thumbs__button--prev';
	private readonly thumbsNextButtonSelector = '.modal-gallery-thumbs__button--next';
	private modalElement: HTMLElement | null;
	private mainSlider: ModalGalleryMain;
	private thumbsSlider: ModalGalleryThumbs;
	private thumbsPrevButton: HTMLButtonElement;
	private thumbsNextButton: HTMLButtonElement;
	private openIndex = 0;
	private isOpen = false;

	constructor(element: HTMLElement) {
		if (!element) return;

		this.element = element;
		this.init();
	}

	public init = (): void => {
		this.modalElement = this.element.closest(this.modalSelector);
		const mainElement = this.element.querySelector<HTMLElement>(this.mainSelector);
		const thumbsElement = this.element.querySelector<HTMLElement>(this.thumbsSelector);
		this.thumbsPrevButton = thumbsElement.querySelector(this.thumbsPrevButtonSelector);
		this.thumbsNextButton = thumbsElement.querySelector(this.thumbsNextButtonSelector);

		this.thumbsSlider = new ModalGalleryThumbs(thumbsElement);
		this.mainSlider = new ModalGalleryMain(mainElement, this.thumbsSlider.slider);
		this.mainSlider.slider.on('slideChange', this.handleMainChange);
		this.thumbsPrevButton.addEventListener('click', this.handleThumbsPrevButtonClick);
		this.thumbsNextButton.addEventListener('click', this.handleThumbsNextButtonClick);
		this.modalElement?.addEventListener('modalOpen', this.handleModalOpen);
		this.modalElement?.addEventListener('modalClose', this.handleModalClose);

		classInstance.set(this.element, { modalGalleryMedia: this });
	};

	public setOpenIndex = (index: number | string | undefined): void => {
		this.openIndex = this.parseIndex(index);
	};

	public getActiveIndex = (): number => this.mainSlider.slider.activeIndex;

	private handleThumbsPrevButtonClick = (): void => {
		this.mainSlider.slider.slidePrev();
	};

	private handleThumbsNextButtonClick = (): void => {
		this.mainSlider.slider.slideNext();
	};

	private handleModalOpen = (): void => {
		this.isOpen = true;

		requestAnimationFrame(() => {
			this.mainSlider.slider.slideTo(this.openIndex, 0);
			this.mainSlider.slider.update();
			this.mainSlider.slider.navigation?.update();
		});
	};

	private handleModalClose = (): void => {
		this.isOpen = false;
	};

	private handleMainChange = (): void => {
		if (!this.isOpen) return;

		const { activeIndex, previousIndex } = this.mainSlider.slider;
		this.openIndex = activeIndex;
		this.checkVideo(previousIndex, activeIndex);
	};

	private checkVideo = (previousIndex: number, activeIndex: number): void => {
		this.mainSlider.slider.slides.forEach(($slide) => {
			$slide.querySelector('video')?.pause();
		});

		const $video = this.mainSlider.slider.slides[activeIndex]?.querySelector('video') as HTMLVideoElement | null;

		if ($video && activeIndex !== previousIndex) {
			$video.play().catch(() => {});
		}
	};

	private parseIndex = (index: number | string | undefined): number => {
		const slideIndex = Number(index);

		return Number.isFinite(slideIndex) ? slideIndex : 0;
	};

	public destroy = (): void => {
		this.mainSlider.slider?.off('slideChange', this.handleMainChange);
		this.thumbsPrevButton?.removeEventListener('click', this.handleThumbsPrevButtonClick);
		this.thumbsNextButton?.removeEventListener('click', this.handleThumbsNextButtonClick);
		this.modalElement?.removeEventListener('modalOpen', this.handleModalOpen);
		this.modalElement?.removeEventListener('modalClose', this.handleModalClose);
		this.mainSlider?.destroy();
		this.thumbsSlider?.destroy();
		classInstance.del(this.element, 'modalGalleryMedia');
	};
}
